/**
 * Pictionary cards — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tabletop_iii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const pictionaryCardsPack: ToilePack = {
    slug: 'pictionary-cards',
    category: 'tabletop',
    name: 'Pictionary cards',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'easy', label: 'Easy words', subject: 'a printable sheet of eight word cards in a 2 by 4 grid with dashed cut lines, each with a pencil icon and one word, "Cat", "Sun", "House", "Tree" and four more'},
        {key: 'hard', label: 'Hard words', subject: 'a printable sheet of eight word cards in a 2 by 4 grid with dashed cut lines, each with a small category tag and a harder word, "Lighthouse", "Volcano", "Astronaut" and five more'},
        {key: 'blank', label: 'Blank cards', subject: 'a printable sheet of eight blank drawing-game cards in a 2 by 4 grid with dashed cut lines, each headed "Draw It" with a pencil icon and an empty line'},
    ],
    styles: [
        {key: 'doodle', label: 'Doodle fun', style: 'a hand-drawn doodle look with wobbly borders and little sketched pencils', palette: 'blue and orange crayon colors on white', framing: 'cards evenly spaced on the page with a wide margin'},
        {key: 'chalk', label: 'Chalkboard', style: 'white chalk lettering and borders as on a classroom blackboard', palette: 'white and pale yellow chalk on a dark green board', framing: 'cards evenly spaced on the page with a wide margin'},
        {key: 'clean', label: 'Clean mono', style: 'clean black outline cards with thin borders and one small pencil icon', palette: 'black ink only', framing: 'cards evenly spaced on the page with a wide margin'},
    ],
    directions: ['animals', 'movies', 'food words'],
    directionHint: 'try: animals',
    seo: {
        title: 'Pictionary cards: easy words, hard words, blank cards',
        metaDescription: 'Printable pictionary word cards in three styles: an easy word sheet, a harder word sheet by category and a blank set of drawing cards.',
        h1: 'Pictionary cards: easy words, harder words, blank drawing cards',
        tagline: 'Nine sheets: easy words, harder words and blank cards, in three styles.',
        intro: 'Pictionary word cards are printed for a drawing game at home or in class, one word to each card. This pack shows a sheet of eight easy words such as Cat and Tree, a sheet of harder words with category tags, and a sheet of blank cards headed Draw It. Each comes as a doodle sheet, a chalkboard or a clean black outline. Type a subject, such as animals, and the sheet keeps its cards.',
        useCases: [
            'a drawing game for a family evening',
            'a class warm-up with one word each',
            'a party game with guests in teams',
            'blank cards for words you choose',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'Three readings in three styles: a sheet of easy words, a sheet of harder words with category tags, and a sheet of blank drawing cards.'},
            {q: 'What is the difference between the easy and hard sheets?', a: 'The easy sheet has everyday words such as Cat, Sun and Tree; the hard sheet has longer words, each with a small category tag.'},
            {q: 'Can I add my own words?', a: 'Yes. The blank sheet has eight cards headed Draw It, each with a pencil icon and an empty line.'},
        ],
    },
}
